import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output,
  ViewChild,
  OnDestroy,
  OnChanges,
  SimpleChanges,

} from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { locale } from 'devextreme/localization';
import { Deferred } from 'devextreme/core/utils/deferred';


import { ICellInfo, IGridColumn,IGridSummary,IGroupItem,ISelectedRange, IButtonConfig} from '../../../modules/tasks/model/types';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { DomSanitizer } from '@angular/platform-browser';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { UserPreferencesService } from '../../services/user_preferences.service';
import { DataGridSelectionService } from '../../services/data-grid-selection.service';
import { Subscription } from 'rxjs';
import { getDayStart, getDayEnd } from '../../../types/resource';



@Component({
  standalone: false,
  selector: 'data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss', '../../../../styles.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataGridComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('dxDataGrid', { static: false }) dataGrid: DxDataGridComponent;

  /** Region-aware date display format for the due-date cell template (dd/MM/yyyy for EU). */
  get dateDisplayFormat(): string {
    return locale() === 'en-US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
  }

  @Input() rowData: any;
  @Input() columnData: IGridColumn[];
  existingColumnNames: string[] = [];
  @Input() childColumnData: IGridColumn[];
  /** Loaded deals list, used to resolve raw deal/facility IDs in the taskData Details column. */
  @Input() deals: any[] = [];
  /** Live search panel text, used to highlight matched values (not JSON keys) in the Detail column. */
  currentSearchText: string = '';
  @Input() childPropertyKey: string;
  @Input() groupItems: IGroupItem[];
  @Input() saveData: Function;
  @Input() refreshMode: string;
  @Input() allowUpdating: boolean = true;
  @Input() allowDeleting: boolean = true;
  @Input() showFilterRow: boolean = false;
  @Input() showSearchPanel: boolean = false;
  @Input() showHeaderFilter: boolean = true;
  @Input() allowEditing: boolean = true;
  @Input() enableFocusedRow: boolean = false;
  @Input() keyExpr: string = 'id';
  @Input() storageKey: string;
  @Input() editExclusions: string[] = [];
  @Input() groupPanelVisible: boolean = true;
  @Input() autoExpandGroups: boolean = true;
  @Input() summaryConfig: IGridSummary[];
  @Input() allowExportSelectedRows: boolean = false;
  @Input() addNewText: string;
  @Input() addNewOptions: Array<{id: number, icon: string, text: string}> ;
  @Input() gridFilterOptions: any[]
  @Input() hasMasterDetail: boolean = false;
  @Input() masterDetailTemplate: any;
  @Input() detailRowHighlightPredicate: ((rowData: any) => boolean) | null = null;
  @Input() rowHighlightPredicate: ((rowData: any) => boolean) | null = null;
  /** When provided, rows failing this predicate cannot be checkbox-selected. */
  @Input() isRowSelectable: ((rowData: any) => boolean) | null = null;
  @Input() searchPlaceholder: string;
  @Input() tasksCheckboxText: string;
  @Input() radioGroupValue: any;
  @Input() cacheEnabled: boolean;
  @Input() datagridButtons: IButtonConfig[];
  @Input() checkbox1Text: string = '';
  @Input() checkbox1Value: boolean = false;
  @Input() checkbox1Visible: boolean = false;
  @Input() checkbox2Text: string = '';
  @Input() checkbox2Value: boolean = false;
  @Input() checkbox2Visible: boolean = false;
  @Input() selectBoxLabel: string = '';
  @Input() selectBoxItems: Array<string | { text?: string; value?: string }> = [];
  @Input() selectBoxValue: string = '';
  @Input() selectBoxVisible: boolean = false;
  @Input() isButtonDisabled: (buttonId: string, rowData: any) => boolean;
  @Input() isButtonVisible: (buttonId: string, rowData: any) => boolean;
  @Input() getButtonHint: (button: any, rowData: any) => string;
  @Input() getButtonClass: (button: any, rowData: any) => string;
  @Input() getButtonIcon: (button: any, rowData: any) => string;
  @Input() currencyFormatter: (row: any) => string;
  @Input() getAmountCellClass: (row: any) => string;
  @Input() getAmountTooltip: ((row: any) => string) | null = null;
  @Input() isUrgentDueDateRow: ((rowData: any) => boolean) | null = null;
  @Input() filteredExternally: boolean = false;
  @Input() filteredExternallyMessage: string = 'Filtered Externally';
  @Input() showAddNew: boolean = false;
  @Input() showExportToExcel: boolean = false;
  @Input() repaintChangesOnly: boolean = true;
  @Input() hightlightChanges: boolean = true;
  /** Explicit DevExtreme grid height — set to pre-allocate space and prevent CLS. */
  @Input() gridHeight: string | number | undefined = undefined;

  /** Responsive search panel width — narrows below 768px so the panel never
   *  clips the left viewport edge. DevExtreme reads this on every resize. */
  get searchPanelWidth(): number {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1366;
    return vw <= 767 ? Math.max(120, vw - 100) : 350;
  }

  /** Hides the group-by panel below 1024px — drag-to-group is not practical
   *  on tablet/mobile and the panel header space is needed by search. */
  get effectiveGroupPanelVisible(): boolean {
    if (typeof window === 'undefined') return this.groupPanelVisible;
    return window.innerWidth >= 1024 && this.groupPanelVisible;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Triggers Angular change detection so searchPanelWidth and
    // effectiveGroupPanelVisible re-evaluate on every resize.
  }

  @Output() onEditorPreparingEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onEditorPreparedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onRowDblClickEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onDetailRowDblClickEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onRowClickEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onDetailRowClickEmitter: EventEmitter<any> = new EventEmitter();
  @Output() rowSaving: EventEmitter<any> = new EventEmitter();
  @Output() onCellPreparedEventEmitter: EventEmitter<DxDataGridTypes.CellPreparedEvent> = new EventEmitter();
  @Output() onDetailCellPreparedEventEmitter: EventEmitter<DxDataGridTypes.CellPreparedEvent> = new EventEmitter();
  @Output() rowSelectionChangedEmitter: EventEmitter<any> = new EventEmitter();
  /** Fires when checkbox selection changes within a specific expanded parent's nested subtask grid. */
  @Output() detailRowSelectionChangedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onCellClickEmitter: EventEmitter<any> = new EventEmitter();
  @Output() reloadDataEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onContextMenuPreparingEmitter: EventEmitter<DxDataGridTypes.ContextMenuPreparingEvent> = new EventEmitter();
  @Output() onDetailContextMenuPreparingEmitter: EventEmitter<DxDataGridTypes.ContextMenuPreparingEvent> = new EventEmitter();
  @Output() onContentReadyEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onAddNewEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onFilterEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onCheckboxChangedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onButtonClickedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onContextMenuHidingEmitter: EventEmitter<any> = new EventEmitter();

  @Output() onCheckbox1ChangedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onCheckbox2ChangedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onSelectBoxChangedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onExternalFilterDismissedEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() onExportingToExcelEmitter: EventEmitter<any> = new EventEmitter();



  focusedRowIndex: number = 0;
  allowSelectAll = true;
  public editedRows: any[] = [];
  loadPanelPosition = { of: '#gridContainer' };
  currentKey: number;
  cellData: ICellInfo[] = [];
  selectedRange: ISelectedRange = {};
  shiftSelectRange: ISelectedRange = {};
  isSelectionStopped!: boolean;
  cellInfos: ICellInfo[] = [];
  numList: any[] = [];
  logComponent: boolean = false;
  selectedSubtotal: number;
  clickedCellElement: any;
  hasExtendedFilters: boolean;
  gridScrollWidth: number = 2000; // Width for custom scrollbar
  expandedRowStorageRowKey: string;
  expandedRowKeys: any[] = [];
  selectedRowsStorageKey: string;
  selectedRowKeys: any[] = [];
  /** Guards against re-entrant onSelectionChanged firing from our own deselectRows() correction call. */
  private isCorrectingSelection: boolean = false;
  /** Same guard as isCorrectingSelection, but per nested detail-grid instance (one per expanded parent row). */
  private detailGridsCorrectingSelection = new WeakSet<any>();
  filterIcon: string = 'filter';
  clearIcon: string = 'collapse';
  resetGridIcon: string = 'clearcircle';
  private isLoadingGridState: boolean = false;

  // Computed once in the constructor (static content, stable onClick closures) rather than as
  // getters — a getter bound in the template is re-evaluated, with a brand-new object each time,
  // on every change-detection pass. Since these feed toolbarItems below via [items], that churn
  // forced DevExtreme's toolbar widget to see a "changed" input and fully tear down and rebuild
  // itself on every single onOptionChanged (e.g. a mere cell hover), producing multi-hundred-ms
  // mouseover/mouseup handlers that starved column-header drag gestures of enough main-thread
  // time to register as a drag instead of a plain click (observed: ghost never tracked the
  // pointer, reorder only committed after 2-3 retries).
  readonly clearAllFiltersButtonOptions: any;
  readonly collapseAllGroupsButtonOptions: any;
  readonly clearGridStateButtonOptions: any;

  // dxi-data-grid-toolbar-item content children silently drop unnamed/custom items in this app;
  // binding the plain array via [items] bypasses that content-projection path entirely. Rebuilt
  // only in ngOnChanges when an input it actually depends on changes — see comment above.
  toolbarItems: any[];

  private buildToolbarItems(): any[] {
    const items: any[] = [
      'groupPanel',
      { location: 'after', name: 'searchPanel' },
      { location: 'after', name: 'columnChooserButton', hint: 'column chooser' },
      { location: 'after', name: 'exportButton', hint: 'export to Excel', visible: this.showExportToExcel },
      { location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: this.clearAllFiltersButtonOptions },
    ];
    if (this.hasMasterDetail) {
      items.push({ location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: this.collapseAllGroupsButtonOptions });
    }
    items.push({ location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: this.clearGridStateButtonOptions });
    return items;
  }

  private isMasterDetailStateRestoring: boolean = false;
  private selectionSubscription: Subscription;

  userPreferenceActions: Array<{id: number, icon: string, class: string}> = [
    { id: 1, icon: "save", class: "dx-button" },
    { id: 2, icon: "clear", class: "dx-button"}

    ];
    dropDownOptions = {
      height: 100,
      width: 60
    }

    addNewDropDownOptions = {
      height: "auto",
      width: 200
    }

    filterDropDownOptions = {
      height: 150,
      width: 200,

    }


  constructor(
    private readonly sanitizer: DomSanitizer,
    private userPreferencesService: UserPreferencesService,
    private selectionService: DataGridSelectionService,
    private ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.loadGridState = this.loadGridState.bind(this);
    this.saveGridState = this.saveGridState.bind(this);
    this.currentKey = 0;
    this.numList = [];
    this.hasExtendedFilters = this.gridFilterOptions == null ? true : false;

    this.clearAllFiltersButtonOptions = {
      icon: this.filterIcon,
      hint: 'clear all filters',
      elementAttr: { 'data-testid': 'data-grid-button-1', class: 'toolbar-circle-button' },
      onClick: () => this.clearAllFilters(),
    };
    this.collapseAllGroupsButtonOptions = {
      icon: this.clearIcon,
      hint: 'collapse all',
      elementAttr: { 'data-testid': 'data-grid-button-2', class: 'toolbar-circle-button' },
      onClick: () => this.collapseAllGroups(),
    };
    this.clearGridStateButtonOptions = {
      icon: this.resetGridIcon,
      hint: 'clear grid layout',
      elementAttr: { 'data-testid': 'data-grid-button-3', class: 'toolbar-circle-button' },
      onClick: () => this.clearGridState(),
    };
    this.toolbarItems = this.buildToolbarItems();

    // Bind scroll event handlers for proper event listener management
    this.boundOnGlobalScroll = this.onGlobalScroll.bind(this);
    this.boundOnGridScroll = this.onGridScroll.bind(this);
    this.boundOnGridWheel = this.onGridWheel.bind(this);
    this.boundOnGridPointerDown = this.onGridPointerDown.bind(this);
    this.boundOnDocumentPointerUp = this.onDocumentPointerUp.bind(this);
  }

  handleButtonClick(e: DxButtonTypes.ClickEvent, button : IButtonConfig, data: any) {
    e.event.stopPropagation();
    e.event.data = {button: button, rowData: data};
    //console.log(button.id);
    this.onButtonClickedEmitter.emit(e);
  }






  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnData']) {
      this.existingColumnNames = this.computeExistingColumnNames();
    }
    if (changes['showExportToExcel'] || changes['hasMasterDetail']) {
      this.toolbarItems = this.buildToolbarItems();
    }
  }

  ngOnInit(): void {
    this.expandedRowStorageRowKey = `${this.storageKey}_expandedRowKey`;
    this.selectedRowsStorageKey = `${this.storageKey}_selectedRowKey`;
    const savedExpandedRows = localStorage.getItem(this.expandedRowStorageRowKey);
    this.expandedRowKeys = savedExpandedRows ? JSON.parse(savedExpandedRows) : [];

    const savedSelectedRowKeys = localStorage.getItem(this.selectedRowsStorageKey);
    this.selectedRowKeys = savedSelectedRowKeys ? JSON.parse(savedSelectedRowKeys) : [];

    // Subscribe to clear selection events
    this.selectionSubscription = this.selectionService.clearSelection$.subscribe(() => {
      this.clearSelection();
      this.cdr.markForCheck();
    });
  }

  // Wraps the DevExtreme instance updateDimensions() with a re-entrancy depth guard.
  // When column fixing is enabled and master-detail rows are present, DevExtreme's
  // _updateFixedMasterDetailGrids calls updateDimensions() on the parent grid again from
  // within the first call's own resolution — legitimate nested calls needed to finish
  // recalculating geometry after a column reorder/resize drag completes. Blocking ALL
  // re-entrant calls (as a plain boolean guard does) skips that necessary recalculation,
  // leaving the drag's header/ghost visually stuck until an unrelated later redraw happens
  // to finish the job. Only unbounded/runaway recursion (the original stack-overflow bug)
  // needs to be stopped, so a depth cap is used instead of an all-or-nothing block.
  //
  // Live telemetry ruled this guard out as the cause of the 2026-10 stuck-drag regression
  // (updateDimensions() wasn't even the bottleneck — see toolbarItems below); maxDepth is
  // kept modestly above the original 3 as cheap insurance, with a one-time warning if it's
  // ever actually hit so a future regression here is visible instead of silently masquerading
  // as success.
  private patchUpdateDimensions(): void {
    const instance = this.dataGrid.instance as any;
    if (!instance || instance.__updateDimensionsPatched) return;
    const original: (...args: unknown[]) => any = instance.updateDimensions.bind(instance);
    const maxDepth = 10;
    // Caps how long a single in-flight call can hold its depth slot before being force-released.
    const releaseTimeoutMs = 1000;
    let depth = 0;
    let hasWarnedAtCap = false;
    instance.updateDimensions = function (...args: unknown[]) {
      if (depth >= maxDepth) {
        if (!hasWarnedAtCap) {
          hasWarnedAtCap = true;
          console.warn(`DataGridComponent: updateDimensions() re-entrancy depth cap (${maxDepth}) reached — recalculation was skipped. If this is legitimate nesting rather than runaway recursion, raise maxDepth further.`);
        }
        // Internal DevExtreme callers (e.g. master-detail dimension sync) call .done()/.fail()
        // directly on this return value, not just .then() — a plain Promise breaks them silently.
        const blocked = Deferred();
        blocked.resolve();
        return blocked.promise();
      }
      depth++;
      let released = false;
      // Safety net: if .always() never fires (e.g. the recalculation is itself superseded by a
      // later overlapping call during continuous drag mousemove updates), depth would otherwise
      // leak upward forever, permanently blocking this grid instance's updateDimensions calls.
      const releaseTimer = setTimeout(() => {
        if (released) return;
        released = true;
        depth--;
      }, releaseTimeoutMs);
      const result = original(...args);
      // Preserve the original Deferred-like shape; only hook completion to decrement depth.
      if (result && typeof result.always === 'function') {
        result.always(() => {
          if (released) return;
          released = true;
          clearTimeout(releaseTimer);
          depth--;
        });
      } else {
        released = true;
        clearTimeout(releaseTimer);
        depth--;
      }
      return result;
    };
    instance.__updateDimensionsPatched = true;
  }

  ngAfterViewInit() {
    if (this.dataGrid?.instance) {
      this.ngZone.runOutsideAngular(() => {
        this.patchUpdateDimensions();
      });
    }

    const dataGridElement = this.dataGrid.instance.element();
    // Capture phase runs before DevExtreme's own (bubble-phase) wheel handler on the scrollable
    // container, so stopPropagation here prevents DevExtreme from also applying its vertical
    // interpretation of the same horizontal wheel/trackpad gesture.
    dataGridElement.addEventListener('wheel', this.boundOnGridWheel, { capture: true, passive: false });
    // Tracks column reorder/resize drags so runGridUpdate() can defer disruptive grid
    // mutations (e.g. SignalR-triggered refreshes) until the gesture completes.
    dataGridElement.addEventListener('pointerdown', this.boundOnGridPointerDown, { capture: true });
    document.addEventListener('pointerup', this.boundOnDocumentPointerUp, { capture: true });
    dataGridElement.addEventListener('touchmove', (args: any) => {
      const event = args.touches[0],
        element = document.elementFromPoint(event.clientX, event.clientY),
        cellInfo = this.cellInfos.filter(
          (x: any) => x.cellElement === element
        )[0];
      if (cellInfo) {
        this.selectedRange.endRowIndex = cellInfo.rowIndex;
        this.selectedRange.endColumnIndex = cellInfo.columnIndex;

      }


    });

    //console.log('🔄 Data grid ngAfterViewInit - Component:', this.storageKey || 'Unknown');

    // Immediately try to show scrollbar for this component
    this.forceShowScrollbar();

    // Set up custom scrollbar synchronization with reduced delay
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.setupCustomScrollbar();
      }, 250); // Reduced delay to get scrollbar working again

      // Also try again with longer delay for notifications component
      setTimeout(() => {
        //console.log('🔄 Second attempt - setupCustomScrollbar for:', this.storageKey || 'Unknown');
        this.setupCustomScrollbar();
      }, 1000);
    });
  }

  // Force show scrollbar immediately when component loads
  private forceShowScrollbar() {
    const globalScrollbar = document.getElementById('globalScrollbar');
    const scrollbarContent = document.getElementById('scrollbarContent');

    //console.log('🚀 Force showing scrollbar for component:', this.storageKey || 'Unknown', {
    //   globalScrollbar: globalScrollbar ? 'Found' : 'NOT FOUND',
    //   scrollbarContent: scrollbarContent ? 'Found' : 'NOT FOUND'
    // });

    if (globalScrollbar && scrollbarContent) {
      // Force show scrollbar immediately
      globalScrollbar.style.display = 'block';
      globalScrollbar.style.visibility = 'visible';
      globalScrollbar.style.opacity = '1';
      globalScrollbar.style.height = '17px';
      globalScrollbar.style.minHeight = '17px';
      globalScrollbar.style.maxHeight = '17px';
      globalScrollbar.style.zIndex = '1000';
      globalScrollbar.style.overflowX = 'auto';
      globalScrollbar.style.overflowY = 'hidden';
      globalScrollbar.style.backgroundColor = '#f8f8f8';
      globalScrollbar.style.border = '1px solid #ddd';
      globalScrollbar.style.transition = 'none';

      // Set a minimum content width
      scrollbarContent.style.width = '2000px';
      scrollbarContent.style.backgroundColor = 'transparent';
      scrollbarContent.style.height = '1px';
      scrollbarContent.style.minHeight = '1px';
      scrollbarContent.style.transition = 'none';

      //console.log('✅ Force showed scrollbar immediately for:', this.storageKey || 'Unknown');
    } else {
      console.warn('⚠️ Could not force show scrollbar - elements not found for:', this.storageKey || 'Unknown');
    }
  }

  // Last resort method to create scrollbar elements if they don't exist
  private createScrollbarElements() {
    //console.log('🆘 Last resort - trying to create scrollbar elements for:', this.storageKey || 'Unknown');

    // Check if elements already exist
    let globalScrollbar = document.getElementById('globalScrollbar');
    let scrollbarContent = document.getElementById('scrollbarContent');

    if (!globalScrollbar) {
      //console.log('📝 Creating globalScrollbar element');
      globalScrollbar = document.createElement('div');
      globalScrollbar.id = 'globalScrollbar';
      globalScrollbar.style.display = 'block';
      globalScrollbar.style.visibility = 'visible';
      globalScrollbar.style.opacity = '1';
      globalScrollbar.style.height = '17px';
      globalScrollbar.style.minHeight = '17px';
      globalScrollbar.style.maxHeight = '17px';
      globalScrollbar.style.zIndex = '1000';
      globalScrollbar.style.overflowX = 'auto';
      globalScrollbar.style.overflowY = 'hidden';
      globalScrollbar.style.backgroundColor = '#f8f8f8';
      globalScrollbar.style.border = '1px solid #ddd';
      globalScrollbar.style.transition = 'none';
      globalScrollbar.style.position = 'fixed';
      globalScrollbar.style.bottom = '20px';
      globalScrollbar.style.left = '0';
      globalScrollbar.style.right = '0';
      globalScrollbar.style.margin = '0 20px';

      // Try to find the app root or body to append to
      const appRoot = document.querySelector('app-root') || document.body;
      appRoot.appendChild(globalScrollbar);
    }

    if (!scrollbarContent) {
      //console.log('📝 Creating scrollbarContent element');
      scrollbarContent = document.createElement('div');
      scrollbarContent.id = 'scrollbarContent';
      scrollbarContent.style.width = '2000px';
      scrollbarContent.style.backgroundColor = 'transparent';
      scrollbarContent.style.height = '1px';
      scrollbarContent.style.minHeight = '1px';
      scrollbarContent.style.transition = 'none';

      if (globalScrollbar) {
        globalScrollbar.appendChild(scrollbarContent);
      }
    }

    if (globalScrollbar && scrollbarContent) {
      //console.log('✅ Successfully created scrollbar elements for:', this.storageKey || 'Unknown');
      // Try setup again
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.setupCustomScrollbar();
        }, 100);
      });
    } else {
      console.error('❌ Failed to create scrollbar elements for:', this.storageKey || 'Unknown');
    }
  }

  private setupCustomScrollbar() {
    if (this.dataGrid) {
      // Wait for grid to render
      this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const gridElement = this.dataGrid.instance.element();

        // Try multiple selectors for DevExtreme scrollable container in order of preference
        let scrollableContainer = gridElement.querySelector('.dx-datagrid-rowsview .dx-scrollable-container');
        if (!scrollableContainer) {
          scrollableContainer = gridElement.querySelector('.dx-scrollable-container');
        }
        if (!scrollableContainer) {
          scrollableContainer = gridElement.querySelector('.dx-datagrid-content .dx-scrollable-container');
        }
        if (!scrollableContainer) {
          scrollableContainer = gridElement.querySelector('.dx-scrollable');
        }
        if (!scrollableContainer) {
          scrollableContainer = gridElement.querySelector('.dx-datagrid-scroll-container');
        }
        if (!scrollableContainer) {
          scrollableContainer = gridElement.querySelector('.dx-scrollable-wrapper');
        }

        // Also try to get the table content for more accurate measurements
        const tableElement = gridElement.querySelector('.dx-datagrid-table');

        const globalScrollbar = document.getElementById('globalScrollbar');
        const scrollbarContent = document.getElementById('scrollbarContent');

        //console.log('🔍 Data grid scrollbar setup for component:', this.storageKey || 'Unknown', {
        //   scrollableContainer: scrollableContainer ? scrollableContainer.className : 'NOT FOUND',
        //   tableElement: tableElement ? 'Found' : 'NOT FOUND',
        //   globalScrollbar: globalScrollbar ? 'Found' : 'NOT FOUND',
        //   scrollbarContent: scrollbarContent ? 'Found' : 'NOT FOUND',
        //   currentRetries: this.scrollbarSetupRetries,
        //   maxRetries: this.maxScrollbarRetries
        // });

        // If scrollbar elements not found, retry with longer delay for production environments
        if (!globalScrollbar || !scrollbarContent) {
          this.scrollbarSetupRetries++;
          if (this.scrollbarSetupRetries <= this.maxScrollbarRetries) {
            console.warn(`⚠️ Scrollbar elements not found for ${this.storageKey || 'Unknown'}, retrying in 1 second... (attempt ${this.scrollbarSetupRetries}/${this.maxScrollbarRetries})`);
            setTimeout(() => {
              this.setupCustomScrollbar();
            }, 1000);
          } else {
            console.error(`❌ Failed to find scrollbar elements after maximum retries for ${this.storageKey || 'Unknown'}. Scrollbar functionality disabled for this grid.`);

            // Last resort - try to create scrollbar elements if they don't exist
            this.createScrollbarElements();
          }
          return;
        }

        // Reset retry counter on success
        this.scrollbarSetupRetries = 0;

        if (scrollableContainer && globalScrollbar && scrollbarContent) {
          // Clean up existing event listeners first to prevent duplicates
          if (this.globalScrollElement) {
            this.globalScrollElement.removeEventListener('scroll', this.boundOnGlobalScroll);
          }
          if (this.gridScrollContainer) {
            this.gridScrollContainer.removeEventListener('scroll', this.boundOnGridScroll);
          }

          // Clean up additional scroll containers
          this.additionalScrollContainers.forEach(container => {
            container.removeEventListener('scroll', this.boundOnGridScroll);
          });
          this.additionalScrollContainers = [];

          // Get the actual scrollable content width more accurately
          let gridContentWidth = scrollableContainer.scrollWidth;
          const gridVisibleWidth = scrollableContainer.clientWidth;

          // Check if table element gives us a better measurement
          if (tableElement) {
            const tableWidth = (tableElement as HTMLElement).scrollWidth || (tableElement as HTMLElement).offsetWidth;
            // Use the larger of the two measurements for accuracy
            gridContentWidth = Math.max(gridContentWidth, tableWidth);
          }

          // Calculate the global scrollbar visible width (should match the grid's visible width)
          const globalScrollbarVisibleWidth = globalScrollbar.clientWidth;

          // Set scrollbar content width to create the same scroll range as the grid
          // The scrollable distance should be: contentWidth - visibleWidth
          const scrollableDistance = gridContentWidth - gridVisibleWidth;
          // Calculate proper content width for synchronization
          // So the global scrollbar content width should be: globalVisibleWidth + scrollableDistance
          this.gridScrollWidth = globalScrollbarVisibleWidth + scrollableDistance;

          // Show/hide scrollbar based on whether this grid needs horizontal scrolling
          // Use a small tolerance to account for rounding differences in different environments
          const scrollTolerance = 2;
          const needsScrolling = gridContentWidth > (gridVisibleWidth + scrollTolerance);

          // Additional comprehensive overflow checks
          const scrollableContainerOverflow = scrollableContainer.scrollWidth > (scrollableContainer.clientWidth + scrollTolerance);
          const tableOverflow = tableElement ? (tableElement as HTMLElement).scrollWidth > ((tableElement as HTMLElement).clientWidth + scrollTolerance) : false;

          // Enhanced detection: Show scrollbar if ANY of these conditions are true
          const shouldShowScrollbar = needsScrolling || scrollableContainerOverflow || tableOverflow;

          //console.log('Scrollbar detection analysis:', {
          //   gridContentWidth,
          //   gridVisibleWidth,
          //   difference: gridContentWidth - gridVisibleWidth,
          //   needsScrolling,
          //   scrollableContainerOverflow,
          //   tableOverflow,
          //   shouldShowScrollbar,
          //   scrollableContainer: {
          //     scrollWidth: scrollableContainer.scrollWidth,
          //     clientWidth: scrollableContainer.clientWidth,
          //     offsetWidth: (scrollableContainer as HTMLElement).offsetWidth
          //   },
          //   tableElement: tableElement ? {
          //     scrollWidth: (tableElement as HTMLElement).scrollWidth,
          //     clientWidth: (tableElement as HTMLElement).clientWidth,
          //     offsetWidth: (tableElement as HTMLElement).offsetWidth
          //   } : null
          // });

          // SIMPLIFIED: Always show scrollbar for any data grid component
          //console.log('✅ Setting up scrollbar for data grid component:', this.storageKey || 'Unknown');

          // Calculate scrollbar width for this grid
          this.gridScrollWidth = Math.max(globalScrollbar.clientWidth + scrollableDistance, globalScrollbar.clientWidth + 100);

          // Always show and configure the scrollbar directly with more aggressive styling
          globalScrollbar.style.display = 'block !important';
          globalScrollbar.style.visibility = 'visible !important';
          globalScrollbar.style.opacity = '1 !important';
          globalScrollbar.style.height = '17px';
          globalScrollbar.style.minHeight = '17px';
          globalScrollbar.style.maxHeight = '17px';
          globalScrollbar.style.zIndex = '1000';
          globalScrollbar.style.overflowX = 'auto';
          globalScrollbar.style.overflowY = 'hidden';
          globalScrollbar.style.backgroundColor = '#f8f8f8';
          globalScrollbar.style.border = '1px solid #ddd';
          globalScrollbar.style.position = 'relative';
          globalScrollbar.style.width = '100%';

          scrollbarContent.style.width = this.gridScrollWidth + 'px';
          scrollbarContent.style.backgroundColor = 'transparent';
          scrollbarContent.style.height = '1px';
          scrollbarContent.style.minHeight = '1px';

          // Add transition prevention to avoid flicker during navigation
          globalScrollbar.style.transition = 'none';
          scrollbarContent.style.transition = 'none';

          // Force reflow to ensure styles are applied
          globalScrollbar.offsetHeight;
          scrollbarContent.offsetWidth;

          //console.log('✅ Scrollbar configured for', this.storageKey || 'Unknown', 'with width:', this.gridScrollWidth + 'px');

          // Try to also notify app component if available
          if ((window as any).appComponent && (window as any).appComponent.registerDataGridScrollbar) {
            //console.log('📝 Also registering with app component for:', this.storageKey || 'Unknown');
            (window as any).appComponent.registerDataGridScrollbar(this.gridScrollWidth, scrollableContainer as HTMLElement);
          } else {
            //console.log('ℹ️ App component not available for', this.storageKey || 'Unknown', 'using direct scrollbar management');
          }

          // Store references for proper event handling
          this.globalScrollElement = globalScrollbar;
          this.gridScrollContainer = scrollableContainer;

          // Add new event listeners
          globalScrollbar.addEventListener('scroll', this.boundOnGlobalScroll);
          scrollableContainer.addEventListener('scroll', this.boundOnGridScroll);

          // Also try to add listener to all potential scrollable elements for better coverage
          const allScrollableElements = gridElement.querySelectorAll('.dx-scrollable-container, .dx-scrollable, .dx-datagrid-content, .dx-datagrid-scroll-container');
          allScrollableElements.forEach((element) => {
            const htmlElement = element as HTMLElement;
            if (element !== scrollableContainer && htmlElement.scrollWidth > htmlElement.clientWidth) {
              htmlElement.addEventListener('scroll', this.boundOnGridScroll);
              this.additionalScrollContainers.push(htmlElement);
            }
          });

          // Force initial synchronization
          this.forceSynchronization();

          // Start periodic sync check to catch any sync losses
          this.startSyncCheck();

          // Remove any existing contentReady listener to prevent duplicates
          this.dataGrid.instance.off('contentReady');

          // Update scrollbar when grid content changes
          this.dataGrid.instance.on('contentReady', () => {
            // Re-query elements in case DOM changed
            const currentScrollableContainer = gridElement.querySelector('.dx-scrollable-container') ||
                                              gridElement.querySelector('.dx-datagrid-rowsview .dx-scrollable-container') ||
                                              gridElement.querySelector('.dx-scrollable');
            const currentTableElement = gridElement.querySelector('.dx-datagrid-table');

            if (currentScrollableContainer) {
              let newContentWidth = currentScrollableContainer.scrollWidth;
              const newVisibleWidth = currentScrollableContainer.clientWidth;

              // Check if table element gives us a better measurement
              if (currentTableElement) {
                const tableWidth = (currentTableElement as HTMLElement).scrollWidth || (currentTableElement as HTMLElement).offsetWidth;
                // Use the larger of the two measurements for accuracy
                newContentWidth = Math.max(newContentWidth, tableWidth);
              }

              // Calculate the global scrollbar visible width
              const globalScrollbarVisibleWidth = globalScrollbar.clientWidth;

              // Calculate proper content width for synchronization
              const scrollableDistance = newContentWidth - newVisibleWidth;
              this.gridScrollWidth = globalScrollbarVisibleWidth + scrollableDistance;

              // Show/hide scrollbar based on whether this grid needs horizontal scrolling
              // Use a small tolerance to account for rounding differences in different environments
              const scrollTolerance = 2;
              const needsScrolling = newContentWidth > (newVisibleWidth + scrollTolerance);

              // Additional comprehensive overflow checks
              const scrollableContainerOverflow = currentScrollableContainer.scrollWidth > (currentScrollableContainer.clientWidth + scrollTolerance);
              const tableOverflow = currentTableElement ? (currentTableElement as HTMLElement).scrollWidth > ((currentTableElement as HTMLElement).clientWidth + scrollTolerance) : false;

              // Enhanced detection: Show scrollbar if ANY of these conditions are true
              const shouldShowScrollbar = needsScrolling || scrollableContainerOverflow || tableOverflow;

              // SIMPLIFIED: Always update scrollbar directly for content changes
              const newScrollbarWidth = Math.max(this.gridScrollWidth, globalScrollbar.clientWidth + 100);
              this.gridScrollWidth = newScrollbarWidth;

              // Always show and update scrollbar directly with aggressive styling
              globalScrollbar.style.display = 'block !important';
              globalScrollbar.style.visibility = 'visible !important';
              globalScrollbar.style.opacity = '1 !important';
              globalScrollbar.style.transition = 'none'; // Prevent transitions during updates
              globalScrollbar.style.position = 'relative';
              globalScrollbar.style.width = '100%';
              scrollbarContent.style.width = newScrollbarWidth + 'px';
              scrollbarContent.style.transition = 'none'; // Prevent transitions during updates

              // Force reflow
              globalScrollbar.offsetHeight;
              scrollbarContent.offsetWidth;

              //console.log('✅ Updated scrollbar on content change for', this.storageKey || 'Unknown', 'with width:', newScrollbarWidth + 'px');

              // Try to also notify app component if available
              if ((window as any).appComponent && (window as any).appComponent.registerDataGridScrollbar) {
                (window as any).appComponent.registerDataGridScrollbar(newScrollbarWidth, currentScrollableContainer as HTMLElement);
              }

              // Update references if container changed
              if (currentScrollableContainer !== this.gridScrollContainer) {
                if (this.gridScrollContainer) {
                  this.gridScrollContainer.removeEventListener('scroll', this.boundOnGridScroll);
                }
                this.gridScrollContainer = currentScrollableContainer;
                currentScrollableContainer.addEventListener('scroll', this.boundOnGridScroll);
              }

              // Force synchronization after content updates
              setTimeout(() => {
                this.forceSynchronization();
              }, 50);
            }
          });
        }
      }, 200); // Reduced timeout since we're calling this multiple times
      });
    } else {
      // dataGrid not available
    }
  }

  private globalScrollElement: any;
  private gridScrollContainer: any;
  private additionalScrollContainers: HTMLElement[] = [];
  private isScrolling = false;
  private scrollbarSetupTimer: any;
  private scrollbarSetupRetries = 0; // Track retry attempts
  private maxScrollbarRetries = 3; // Maximum retry attempts
  // Last user-driven horizontal scroll position, used to restore scroll after a content redraw (e.g. expandRow) resets it to 0
  private lastKnownScrollLeft = 0;
  // Scroll position captured just before a row expand/collapse, since DevExtreme's own reset fires a
  // native scroll event that would otherwise overwrite lastKnownScrollLeft before we can restore it
  private scrollLeftBeforeExpand: number | null = null;

  // Store bound function references for proper event listener removal
  private boundOnGlobalScroll: any;
  private boundOnGridScroll: any;
  private boundOnGridWheel: any;
  private boundOnGridPointerDown: any;
  private boundOnDocumentPointerUp: any;
  private syncCheckInterval: any;

  // Interaction guard: true while the user has a pointer down somewhere on the grid
  // (column reorder/resize drag), used to defer disruptive updates via runGridUpdate().
  private isPointerDownInGrid = false;
  private deferredGridUpdates: Array<() => void> = [];
  private pointerSafetyTimeout: any;

  // Force synchronization between scrollbars
  private forceSynchronization() {
    // Programmatically repositioning the scrollable mid-drag confuses DevExtreme's column
    // reorder/resize tracking (its header drag indicator can freeze in place) — skip this
    // tick entirely while the user has a pointer down on the grid; it resumes once released.
    if (this.isPointerDownInGrid) {
      return;
    }
    if (this.globalScrollElement && this.gridScrollContainer) {
      const gridScrollLeft = this.gridScrollContainer.scrollLeft;

      // A content redraw (e.g. expandRow for a subtask) can reset the grid's scrollable
      // container back to 0 even though the user hadn't scrolled back to the start.
      // Restore the last known position instead of propagating the reset.
      if (gridScrollLeft === 0 && this.lastKnownScrollLeft > 1) {
        this.gridScrollContainer.scrollLeft = this.lastKnownScrollLeft;
        this.globalScrollElement.scrollLeft = this.lastKnownScrollLeft;
        this.dataGrid?.instance.getScrollable()?.scrollTo({ left: this.lastKnownScrollLeft });
        return;
      }

      // Sync scroll positions in case they got out of sync
      const globalScrollLeft = this.globalScrollElement.scrollLeft;

      if (Math.abs(gridScrollLeft - globalScrollLeft) > 1) {
        this.globalScrollElement.scrollLeft = gridScrollLeft;
      }
    }
  }

  // Start periodic synchronization check
  private startSyncCheck() {
    // Clear any existing interval
    if (this.syncCheckInterval) {
      clearInterval(this.syncCheckInterval);
    }

    // Check sync every 2 seconds
    this.ngZone.runOutsideAngular(() => {
      this.syncCheckInterval = setInterval(() => {
        this.forceSynchronization();
      }, 2000);
    });
  }

  // Stop periodic synchronization check
  private stopSyncCheck() {
    if (this.syncCheckInterval) {
      clearInterval(this.syncCheckInterval);
      this.syncCheckInterval = null;
    }
  }

  // Debug method to test scrollbar synchronization - call this from browser console
  public testScrollbarSync() {
    //console.log('Testing scrollbar synchronization...');
    if (this.globalScrollElement && this.gridScrollContainer) {
      //console.log('Global scrollbar element:', this.globalScrollElement);
      //console.log('Grid scroll container:', this.gridScrollContainer);
      //console.log('Grid scroll container class:', this.gridScrollContainer.className);
      //console.log('Global scroll width:', this.globalScrollElement.scrollWidth);
      //console.log('Grid scroll width:', this.gridScrollContainer.scrollWidth);
      //console.log('Additional containers:', this.additionalScrollContainers.length);

      // Test setting global scrollbar position
      //console.log('Setting global scrollbar to 100px...');
      this.globalScrollElement.scrollLeft = 100;

      setTimeout(() => {
        //console.log('After global scroll - Grid position:', this.gridScrollContainer.scrollLeft);

        // Check additional containers
        this.additionalScrollContainers.forEach((container, index) => {
          //console.log(`Additional container ${index} scroll position:`, container.scrollLeft);
        });

        // Test setting grid position
        //console.log('Setting grid scroll to 200px...');
        this.gridScrollContainer.scrollLeft = 200;

        setTimeout(() => {
          //console.log('After grid scroll - Global position:', this.globalScrollElement.scrollLeft);
          //console.log('Test complete. Both directions should work if values match.');

          // Try DevExtreme's scroll method
          if (this.dataGrid && this.dataGrid.instance) {
            try {
              //console.log('Testing DevExtreme scrollTo...');
              this.dataGrid.instance.getScrollable()?.scrollTo({ left: 300 });
              setTimeout(() => {
                //console.log('After DevExtreme scrollTo - Global position:', this.globalScrollElement.scrollLeft);
              }, 100);
            } catch (error) {
              //console.log('DevExtreme scrollTo failed:', error);
            }
          }
        }, 100);
      }, 100);
    } else {
      //console.log('Elements not found:', {
      //   global: !!this.globalScrollElement,
      //   grid: !!this.gridScrollContainer
      // });
    }
  }

  private onGlobalScroll(e: Event) {
    if (this.isScrolling) return;
    this.isScrolling = true;

    try {
      const globalScrollElement = e.target as HTMLElement;
      if (!globalScrollElement || !this.gridScrollContainer) {
        return;
      }

      const scrollLeft = globalScrollElement.scrollLeft;
      this.lastKnownScrollLeft = scrollLeft;

      // Update primary grid container
      this.gridScrollContainer.scrollLeft = scrollLeft;

      // Update any additional scroll containers
      this.additionalScrollContainers.forEach((container, index) => {
        container.scrollLeft = scrollLeft;
      });

      // Also try to force scroll on the grid instance itself
      if (this.dataGrid && this.dataGrid.instance) {
        try {
          // Try using DevExtreme's scrollTo method
          this.dataGrid.instance.getScrollable()?.scrollTo({ left: scrollLeft });
        } catch (error) {
          // DevExtreme scrollTo not available
        }
      }

    } catch (error) {
      console.error('Error in global scroll handler:', error);
    } finally {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.isScrolling = false;
        }, 10);
      });
    }
  }

  private onGridScroll(e: Event) {
    if (this.isScrolling) return;
    this.isScrolling = true;

    try {
      const gridContainer = e.target as HTMLElement;
      if (!gridContainer || !this.globalScrollElement) {
        return;
      }

      const scrollLeft = gridContainer.scrollLeft;
      this.lastKnownScrollLeft = scrollLeft;

      this.globalScrollElement.scrollLeft = scrollLeft;
    } catch (error) {
      console.error('Error in grid scroll handler:', error);
    } finally {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.isScrolling = false;
        }, 10);
      });
    }
  }

  // A dedicated horizontal mouse wheel or trackpad swipe reports as deltaX with little/no deltaY;
  // only intercept that case so a normal vertical wheel gesture is left to scroll the rows natively.
  private onGridWheel(e: WheelEvent) {
    const absDeltaX = Math.abs(e.deltaX);
    const absDeltaY = Math.abs(e.deltaY);
    if (absDeltaX === 0 || absDeltaX <= absDeltaY) {
      return;
    }

    const container = this.gridScrollContainer;
    if (!container) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    container.scrollLeft = container.scrollLeft + e.deltaX;
  }

  private onGridPointerDown(): void {
    this.isPointerDownInGrid = true;
    // Fallback in case pointerup is swallowed (e.g. focus stolen by a dialog mid-drag).
    clearTimeout(this.pointerSafetyTimeout);
    this.pointerSafetyTimeout = setTimeout(() => this.forceEndInteraction(), 15000);
  }

  private onDocumentPointerUp(): void {
    if (!this.isPointerDownInGrid) {
      return;
    }
    this.isPointerDownInGrid = false;
    clearTimeout(this.pointerSafetyTimeout);
    // Grace period lets DevExtreme's own drop-commit (columns option change + state save) finish first.
    setTimeout(() => this.flushDeferredGridUpdates(), 100);
  }

  private forceEndInteraction(): void {
    this.isPointerDownInGrid = false;
    this.flushDeferredGridUpdates();
  }

  private flushDeferredGridUpdates(): void {
    if (this.deferredGridUpdates.length === 0) {
      return;
    }
    const updates = this.deferredGridUpdates;
    this.deferredGridUpdates = [];
    updates.forEach(updateFn => updateFn());
  }

  /**
   * Runs a disruptive grid mutation (refresh, dataSource swap, etc.) immediately, or
   * defers it until any in-progress column reorder/resize drag completes — such a
   * mutation mid-drag invalidates the DOM nodes DevExtreme's drag tracking relies on,
   * aborting the gesture.
   */
  public runGridUpdate(updateFn: () => void): void {
    if (this.isPointerDownInGrid) {
      this.deferredGridUpdates.push(updateFn);
      return;
    }
    updateFn();
  }

  /**
   * @deprecated This mutated `this.datagridButtons` in-place and returned the same
   * array reference, causing every row to display the last-evaluated row's button
   * state and unnecessary DxButtonComponent change detection cycles.
   * Use the `gridButtons` pipe (`GridButtonsPipe`) in the template instead.
   */
  getButtons(rd: any)
  {

    if(this.datagridButtons != null)
    {
      this.datagridButtons.forEach((button: IButtonConfig) => {
        button.hint = this.getButtonHint(button, rd);
        button.disabled = this.isButtonDisabled(button.id, rd);
        button.icon = this.getButtonIcon(button, rd);
        button.cssClass = this.getButtonClass(button, rd)});


      return this.datagridButtons;

    }

    return null;
  }

  getStatusClass(status: string): string {
    if (!status) return '';

    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '-');

    switch (normalizedStatus) {
      case 'open':
        return 'status-open';
      case 'closed':
        return 'status-closed';
      case 'cancelled':
        return 'status-cancelled';
      case 'pending':
        return 'status-pending';
      case 'pending-client':
        return 'status-pending-client';
      case 'client-hold':
        return 'status-client-hold';
      case 'rejected':
        return 'status-rejected';
      case 'not-applicable':
        return 'status-not-applicable';
      default:
        return 'status-open'; // Default to open style for unknown statuses
    }
  }

  // Description is redundant when it just restates the row's own Task Type / Request Type,
  // whether shown alone or as the "{workflow} - {name}" combined label (see TaskDashboardService).
  isRedundantDescription(rowData: any): boolean {
    const description = rowData?.description;
    if (!description) return false;

    const name = rowData?.name;
    const workflow = rowData?.workflow;
    const combinedLabel = workflow && name ? `${workflow} - ${name}` : null;

    return description === name || description === workflow || description === combinedLabel;
  }

  // Detail column is intentionally left blank for this request type per business requirement.
  isDetailColumnHidden(rowData: any): boolean {
    return rowData?.workflow === 'LS&B' && rowData?.name === 'Daily Cashflow Report';
  }

  getDueDateClass(dueDate: string | Date, rowData?: any): string {
    if (!dueDate) return '';

    // Don't apply color coding if task is closed or not applicable
    if (rowData && rowData.status) {
      const status = rowData.status.toLowerCase();
      if (status === 'closed' || status === 'not applicable' || status === 'n/a') {
        return '';
      }
    }

    const dt = new Date(dueDate);
    // Check if urgent override applies (overrides date-based styling)
    if (this.isUrgentDueDateRow && rowData && this.isUrgentDueDateRow(rowData)) {
      return 'due-date-overdue';
    }

    const today = new Date();

    // Set both to start of day for accurate comparison
    dt.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // Check if overdue (past due date)
    if (dt < today) {
      return 'due-date-overdue';
    }

    // Check if due today
    if (dt.getTime() === today.getTime()) {
      return 'due-date-today';
    }

    // Future date - no special styling
    return '';

  }

  trackButtonById(_index: number, btn: IButtonConfig): string {
    return btn.id;
  }

  onContextMenuPreparing(e: DxDataGridTypes.ContextMenuPreparingEvent)
  {
    this.onContextMenuPreparingEmitter.emit(e);
  }

  onDetailContextMenuPreparing(e: DxDataGridTypes.ContextMenuPreparingEvent)
  {
    this.onDetailContextMenuPreparingEmitter.emit(e);
  }

  onContextMenuHiding(e: any)
  {
    this.onContextMenuHidingEmitter.emit(e);
  }

  onCheckboxChanged(e: any)
  {
    this.onCheckboxChangedEmitter.emit(e);
  }

  onCheckbox1Changed(e: any)
  {
    this.onCheckbox1ChangedEmitter.emit(e);
  }
  onCheckbox2Changed(e: any)
  {
    this.onCheckbox2ChangedEmitter.emit(e);
  }

  onSelectBoxChanged(e: any)
  {
    this.onSelectBoxChangedEmitter.emit(e);
  }

  private filtersCleared: boolean = false;
  private debounceTimer: any;

  onOptionChanged(e: any) {
    // Handle search panel changes
    if (e.fullName === 'searchPanel.text') {
      // Set synchronously (outside the debounce below) so OnPush change detection picks up the
      // new value immediately and the Detail column's value highlight stays in sync while typing.
      this.currentSearchText = e.value || '';
      clearTimeout(this.debounceTimer);
      this.ngZone.runOutsideAngular(() => {
        this.debounceTimer = setTimeout(() => {
        if (e.value && !this.filtersCleared) {
          this.filtersCleared = true;
          const columns = this.dataGrid.instance.getVisibleColumns();
          //clear column filters
          columns.forEach((col: any) => {
            if (col.dataField) {
                this.dataGrid.instance.columnOption(col.dataField, {
                  filterValue: null,
                  selectedFilterOperation: null
                });
                this.dataGrid.instance.clearFilter("row"); // not the search panel text filter
                this.dataGrid.instance.clearFilter("header");
                this.dataGrid.instance.clearFilter("filter");
              }
          });
        }
        if (e.value !== e.previousValue) {
          this.filtersCleared = false;
        }

        // Re-establish scrollbar after search changes
        this.setupCustomScrollbar();
        }, 300); // 300ms debounce
      });
    }

    // Handle filter changes (header filters, row filters, etc.)
    if (e.fullName && (
        e.fullName.includes('filterValue') ||
        e.fullName.includes('filterOperation') ||
        e.fullName.includes('selectedFilterOperation') ||
        e.fullName === 'filterPanel.filterEnabled' ||
        e.fullName === 'headerFilter.visible' ||
        e.fullName === 'filterRow.visible'
      )) {
      // Re-establish scrollbar after filter changes
      this.setupCustomScrollbar();
    }

    // Handle sorting changes which can also affect scrollbar
    if (e.fullName && e.fullName.includes('sortOrder')) {
      // Re-establish scrollbar after sort changes
      this.setupCustomScrollbar();
    }
  }

  rowPreparedHandler(e: any): void {
    if (e.rowType === 'data' && this.hasMasterDetail) {
      const hasDetails = this.getChildDetails(e.data)?.length > 0;
      const expandIcon = e.rowElement.querySelector('.dx-datagrid-expand');
      if (!hasDetails) {
        e.rowElement.classList.add('no-detail-row');
        if (expandIcon) {
          expandIcon.style.visibility = 'hidden'; // Make the chevron invisible instead of hiding it
        }
      }
    }
  }

  onEditorPreparing(e: any) {
    this.onEditorPreparingEmitter.emit(e);
  }

  onEditorPrepared(e: any) {
    this.onEditorPreparedEmitter.emit(e);
  }



  private isRowDblClick: boolean = false;

  onRowClick(e: any) {
    if (e.rowType !== 'data') return;
    this.isRowDblClick = false;
    setTimeout(() => {
      if (!this.isRowDblClick) {
        // Always select the clicked row, regardless of current selection
        // if (this.dataGrid && this.dataGrid.instance) {
        //   // Clear current selection and select the clicked row
        //   this.dataGrid.instance.selectRows([e.key], false); // false = replace selection
        // }

        // this.saveSelectedRowState(e.key);
        this.onRowClickEmitter.emit(e);
      }}, 500); // Delay to allow for double-click detection
  }

  onRowDblClick(e: any) {
    this.isRowDblClick = true;
    if (e.rowType !== 'data') return;
    this.saveSelectedRowState(e.key);
    this.onRowDblClickEmitter.emit(e);
  }

  private isDetailRowDblClick: boolean = false;

  onDetailRowClick(e: any,masterDetails) {

    if (e.rowType !== 'data') return;
    this.isDetailRowDblClick = false;
    setTimeout(() => {
      if(!this.isDetailRowDblClick) {
      this.saveSelectedRowState(e.key.id);
      e.data.masterDetails = masterDetails;
      this.onDetailRowClickEmitter.emit(e);
      }
    }, 500);
  }

  onDetailRowDblClick(e: any,masterDetails) {
    this.isDetailRowDblClick = true;
    if (e.rowType !== 'data') return;
    this.saveSelectedRowState(e.key.id);
    e.data.masterDetails = masterDetails;
    this.onDetailRowDblClickEmitter.emit(e);
  }

  saveSelectedRowState(rowKey: string){
    this.selectedRowKeys.length = 0;
    this.selectedRowKeys.push(rowKey);
    this.saveSelectedRowsState();
  }

  /**
   * Clear all row selections and localStorage state
   */
  public clearSelection(): void {
    //console.log('🧹 Clearing data grid selection for:', this.storageKey || 'Unknown');

    // Clear the grid selection
    if (this.dataGrid && this.dataGrid.instance) {
      this.dataGrid.instance.clearSelection();
    }

    // Clear internal state
    this.selectedRowKeys = [];

    // Clear localStorage
    localStorage.removeItem(this.selectedRowsStorageKey);

    // Clear the grid state selection as well
    const currentState = this.dataGrid?.instance.state();
    if (currentState && currentState.selectedRowKeys) {
      currentState.selectedRowKeys = [];
      this.saveGridState(currentState);
    }
  }

  onAddNew(e: any)
  {
    this.onAddNewEmitter.emit(e);
  }

  onFilter(e: any)
  {
    this.onFilterEmitter.emit(e);

    // Re-establish scrollbar synchronization after filter changes
    this.setupCustomScrollbar();
  }

  getChildDetails(parent: any) {
    for(const prop in parent)
    {
      if(prop == this.childPropertyKey)
      {
          return parent[prop];
      }
    }

    return [];

  }

  saveDatagridViewPreferences()
  {
    localStorage.getItem("userPreferences");
  }

  userPreferencesSelectionChanged(e: any)
  {
    if(e.itemData.id ==1)
    {
      this.saveDatagridViewPreferences();
    }
    else
    {
      this.clearDatagridViewPreferences();
    }
  }

  clearDatagridViewPreferences() {
    if (localStorage.getItem(this.storageKey) !== null) {
      localStorage.removeItem(this.storageKey);
      this.dataGrid.instance.state(null);
    }
  }


  private isStateLoaded: boolean = false;
  private isApplyingContentReady: boolean = false;

  onContentReady(e: DxDataGridTypes.ContentReadyEvent){
    if (this.isApplyingContentReady) {
      return;
    }
    this.isApplyingContentReady = true;
    try {
      this._onContentReadyImpl(e);
    } finally {
      this.isApplyingContentReady = false;
    }
  }

  private _onContentReadyImpl(e: DxDataGridTypes.ContentReadyEvent): void {
    this.ngZone.runOutsideAngular(() => {
      // Restore the position captured before a row expand/collapse, before the browser paints
      // whatever position DevExtreme's own redraw left the scrollable container at.
      if (this.scrollLeftBeforeExpand !== null) {
        const restoreScrollLeft = this.scrollLeftBeforeExpand;
        this.scrollLeftBeforeExpand = null;
        this.reassertScrollLeft(restoreScrollLeft);
      } else if (this.lastKnownScrollLeft > 1 && (this.gridScrollContainer?.scrollLeft ?? 0) === 0) {
        // Column resize has no dedicated DevExtreme event to capture a "before" position from (unlike
        // row expand/collapse), so detect its same async reset-to-0 here and restore it the same way.
        this.reassertScrollLeft(this.lastKnownScrollLeft);
      }

      if (this.expandedRowKeys.length > 0 && !this.isStateLoaded) {
        this.isStateLoaded = true;
        // Defer expandRow calls to avoid re-triggering contentReady mid-resize
        const keysToExpand = [...this.expandedRowKeys];
        setTimeout(() => {
          keysToExpand.forEach(key => {
            if (!this.dataGrid.instance.isRowExpanded(key)) {
              this.dataGrid.instance.expandRow(key);
            }
          });
        }, 0);
      }

      // Only restore selection from localStorage if we're not loading from grid state
      // Grid state takes precedence over localStorage selection
      const gridState = this.userPreferencesService.loadControlState(this.storageKey);
      let hasGridStateSelection = false;

      if (gridState) {
        try {
          const parsedState = JSON.parse(gridState);
          hasGridStateSelection = parsedState && parsedState.selectedRowKeys && parsedState.selectedRowKeys.length > 0;
        } catch (error) {
          // If we can't parse the state, assume no selection in grid state
          hasGridStateSelection = false;
        }
      }

      // Only restore from localStorage if grid state doesn't have selection and we're not currently loading grid state
      // Also avoid restoration during master-detail state restoration
      if (this.selectedRowKeys.length > 0 && !this.isStateLoaded && !hasGridStateSelection &&
          !this.isLoadingGridState && !this.isMasterDetailStateRestoring) {
        this.dataGrid.instance.selectRows(this.selectedRowKeys, true);
        //console.log('Restored selection from localStorage');
      } else if (hasGridStateSelection || this.isLoadingGridState || this.isMasterDetailStateRestoring) {
        //console.log('Using selection from grid state or currently loading, ignoring localStorage selection');
      }

      this.isStateLoaded = true;

      // Re-establish scrollbar synchronization after content is ready
      // This ensures scrollbar works after page refresh or data reload
      this.setupCustomScrollbar();
    });

    this.onContentReadyEmitter.emit(e);
  }

  onRowExpanding(e: any): void {
    // onRowExpanding only provides e.key, not e.data. Look up the row data
    // from the visible rows so we can check whether it has child details.
    const visibleRow = this.dataGrid?.instance?.getVisibleRows()
      ?.find(r => r.key === e.key);
    const hasDetails = this.getChildDetails(visibleRow?.data)?.length > 0;
    if (!hasDetails) {
      e.cancel = true;
      // Clean up stale localStorage entry so we don't try again next load
      this.expandedRowKeys = this.expandedRowKeys.filter(key => key !== e.key);
      this.saveExpandedRowsState();
      return;
    }
    this.captureScrollLeftBeforeRedraw();
    if (!this.expandedRowKeys.includes(e.key)) {
      this.expandedRowKeys.push(e.key);
      this.saveExpandedRowsState();
    }
  }

  onRowCollapsing(e: any): void {
    this.captureScrollLeftBeforeRedraw();
    this.expandedRowKeys = this.expandedRowKeys.filter(key => key !== e.key);
    this.saveExpandedRowsState();
  }

  private captureScrollLeftBeforeRedraw(): void {
    this.scrollLeftBeforeExpand = this.globalScrollElement?.scrollLeft ?? this.gridScrollContainer?.scrollLeft ?? null;
  }

  private applyScrollLeft(scrollLeft: number): void {
    if (this.gridScrollContainer) {
      this.gridScrollContainer.scrollLeft = scrollLeft;
    }
    if (this.globalScrollElement) {
      this.globalScrollElement.scrollLeft = scrollLeft;
    }
    this.lastKnownScrollLeft = scrollLeft;
    this.dataGrid?.instance.getScrollable()?.scrollTo({ left: scrollLeft });
  }

  // Fixed-column master-detail grids recalculate dimensions asynchronously after contentReady
  // (see patchUpdateDimensions) and can reset scroll again after our first restore, so keep
  // re-applying the target position for a short window until DevExtreme's redraw settles.
  private reassertScrollLeft(scrollLeft: number): void {
    this.applyScrollLeft(scrollLeft);
    this.ngZone.runOutsideAngular(() => {
      [16, 50, 100, 200, 350, 600].forEach(delay => {
        setTimeout(() => this.applyScrollLeft(scrollLeft), delay);
      });
    });
  }

  /**
   * Ensures an array of keys contains only primitive values, not object references.
   * Prevents circular reference errors when serializing to JSON.
   */
  private extractPrimitiveKeys(keys: any[]): any[] {
    return keys.map(key => {
      // If key is an object, extract the actual key value using keyExpr
      if (typeof key === 'object' && key !== null) {
        return key[this.keyExpr];
      }
      // Otherwise it's already a primitive, return as-is
      return key;
    }).filter(key => key !== undefined && key !== null);
  }

  private saveExpandedRowsState(): void {
    const primitiveKeys = this.extractPrimitiveKeys(this.expandedRowKeys);
    localStorage.setItem(this.expandedRowStorageRowKey, JSON.stringify(primitiveKeys));
  }






  onCellPrepared(e: DxDataGridTypes.CellPreparedEvent)
  {
    // DevExtreme sets visibility:hidden as an inline style on the expand-column
    // placeholder cell for rows that have no subtasks. This makes the cell
    // transparent so horizontally scrolled content bleeds through the fixed-column
    // overlay. Force the cell visible and give it an opaque background here,
    // because inline styles cannot be overridden by any stylesheet rule.
    if (e.rowType === 'data' && e.column?.type === 'detailExpand') {
      e.cellElement.style.setProperty('visibility', 'visible', 'important');
      // The cell itself should be opaque; the inner expand indicator will be
      // hidden by the CSS rule targeting .dx-datagrid-expand-indicator.
      if (!e.cellElement.style.backgroundColor) {
        e.cellElement.style.backgroundColor = 'inherit';
      }
    }

    if (e.rowType === 'data' && this.rowHighlightPredicate?.(e.data)) {
      e.cellElement.style.setProperty('background-color', '#ffe4e8', 'important');
    }

    // Disable the checkbox for rows the predicate excludes, so they can never be checked in the
    // first place (onSelectionChanged still backstops Select-All and keyboard selection).
    if (e.rowType === 'data' && (e.column as any)?.command === 'select' && this.isRowSelectable?.(e.data) === false) {
      const checkbox = e.cellElement.querySelector('.dx-checkbox');
      if (checkbox) {
        checkbox.classList.add('dx-state-disabled');
        (checkbox as HTMLElement).style.pointerEvents = 'none';
      }
    }

    // DevExtreme auto-sets title to the column's raw field value; for 'taskData' that is
    // unparsed JSON (including internal GUIDs), so suppress it rather than expose raw data.
    if (e.rowType === 'data' && e.column?.dataField === 'taskData') {
      e.cellElement.removeAttribute('title');
    }
    this.onCellPreparedEventEmitter.emit(e);
  }


  onDetailCellPrepared(e: DxDataGridTypes.CellPreparedEvent)
  {
    if (e.rowType === 'data' && this.detailRowHighlightPredicate?.(e.data)) {
      e.cellElement.style.setProperty('background-color', '#fce4ec', 'important');
    }

    // Disable the checkbox for rows the predicate excludes, so they can never be checked in the
    // first place (onDetailSelectionChanged still backstops Select-All and keyboard selection).
    if (e.rowType === 'data' && (e.column as any)?.command === 'select' && this.isRowSelectable?.(e.data) === false) {
      const checkbox = e.cellElement.querySelector('.dx-checkbox');
      if (checkbox) {
        checkbox.classList.add('dx-state-disabled');
        (checkbox as HTMLElement).style.pointerEvents = 'none';
      }
    }

    if (e.rowType === 'data' && e.column?.dataField === 'taskData') {
      e.cellElement.removeAttribute('title');
    }
    this.onDetailCellPreparedEventEmitter.emit(e);
  }

  onDetailRowPrepared(e: any): void {
    // highlight handled per-cell in onDetailCellPrepared
  }

  // One nested dx-data-grid instance exists per expanded parent row; masterTaskData identifies
  // which parent's subtasks were (de)selected so the consumer can attribute the selection.
  public onDetailSelectionChanged(e: any, masterTaskData: any) {
    if (this.detailGridsCorrectingSelection.has(e.component)) {
      return;
    }

    if (this.isRowSelectable) {
      const ineligibleKeys = (e.selectedRowsData ?? [])
        .filter((row: any) => this.isRowSelectable(row) === false)
        .map((row: any) => row[this.keyExpr]);

      if (ineligibleKeys.length > 0) {
        this.detailGridsCorrectingSelection.add(e.component);
        e.component.deselectRows(ineligibleKeys).then(() => {
          this.detailGridsCorrectingSelection.delete(e.component);
          this.onDetailSelectionChanged({
            component: e.component,
            selectedRowKeys: e.component.getSelectedRowKeys(),
            selectedRowsData: e.component.getSelectedRowsData(),
          }, masterTaskData);
        });
        return;
      }
    }

    this.detailRowSelectionChangedEmitter.emit({
      masterTaskData,
      component: e.component,
      selectedRowKeys: e.selectedRowKeys,
      selectedRowsData: e.selectedRowsData,
    });
  }


  public refreshDataGrid() {
    this.dataGrid.instance.refresh();
  }

  public getSelectedRows() {
    return this.dataGrid.instance.getSelectedRowsData();
  }

  public getRowIndexByKey(key: any) {
    return this.dataGrid.instance.getRowIndexByKey(key);
  }

  public onSaving(e: any) {
    this.rowSaving.emit(e);
  }

  public onRowUpdating(e: any) {
  }

  public onRowUpdated(e: any) {
  }

  public onSelectionChanged(e: any) {
    // Ignore the recursive event deselectRows() below fires while we're mid-correction;
    // the .then() continuation re-reads the final selection and re-enters this method.
    if (this.isCorrectingSelection) {
      return;
    }

    if (this.isRowSelectable) {
      const ineligibleKeys = (e.selectedRowsData ?? [])
        .filter((row: any) => this.isRowSelectable(row) === false)
        .map((row: any) => row[this.keyExpr]);

      if (ineligibleKeys.length > 0) {
        this.isCorrectingSelection = true;
        this.dataGrid.instance.deselectRows(ineligibleKeys).then(() => {
          this.isCorrectingSelection = false;
          this.onSelectionChanged({
            selectedRowKeys: this.dataGrid.instance.getSelectedRowKeys(),
            selectedRowsData: this.dataGrid.instance.getSelectedRowsData(),
          });
        });
        return;
      }
    }

    // Store the selected row keys
    this.selectedRowKeys = e.selectedRowKeys;

    // Don't save to localStorage during grid state loading, initial state restoration,
    // or master-detail state restoration
    if (!this.isLoadingGridState && !this.isMasterDetailStateRestoring && this.isStateLoaded) {
      this.saveSelectedRowsState();
      //console.log('Saved selection change to localStorage');

      // Also force save the current grid state to ensure it's up to date
      // This prevents old grid state from overriding current selection
      setTimeout(() => {
        if (this.dataGrid && this.dataGrid.instance) {
          const currentState = this.dataGrid.instance.state();
          this.saveGridState(currentState);
          //console.log('Updated grid state with current selection');
        }
      }, 100);
    } else {
      //console.log('Skipped saving selection during state loading/restoration', {
      //   isLoadingGridState: this.isLoadingGridState,
      //   isMasterDetailStateRestoring: this.isMasterDetailStateRestoring,
      //   isStateLoaded: this.isStateLoaded
      // });
    }

    this.rowSelectionChangedEmitter.emit(e);
  }

  private saveSelectedRowsState(): void {
    const primitiveKeys = this.extractPrimitiveKeys(this.selectedRowKeys);
    localStorage.setItem(this.selectedRowsStorageKey, JSON.stringify(primitiveKeys));
  }

  /** Force-reloads saved state for the current storageKey into the grid instance. */
  public reloadGridState(): void {
    if (this.dataGrid?.instance) {
      const state = this.loadGridState();
      this.dataGrid.instance.state(state ?? null);
    }
  }

  loadGridState(): any {
    const state = this.userPreferencesService.loadControlState(this.storageKey);

    // Set flag to indicate we're loading grid state
    this.isLoadingGridState = true;

    // For master-detail grids, set additional flag
    if (this.hasMasterDetail) {
      this.isMasterDetailStateRestoring = true;
    }

    // Parse once so we can inspect and return a proper object.
    // DevExtreme's customLoad MUST receive an object (or Promise<object>), NOT a JSON
    // string — passing a string prevents visibleIndex from being applied, which means
    // user column reordering is silently ignored on reload.
    let parsedState: any = null;
    if (state) {
      try {
        parsedState = typeof state === 'string' ? JSON.parse(state) : state;
        if (parsedState && parsedState.selectedRowKeys) {
          // Grid state contains selection info, so clear our localStorage selection to avoid conflicts
          this.selectedRowKeys = [];
          localStorage.removeItem(this.selectedRowsStorageKey);
        }
      } catch (error) {
        console.warn('Error parsing grid state:', error);
        // If the state is invalid, clear it
        this.userPreferencesService.saveControlState(this.storageKey, null);
        this.isLoadingGridState = false;
        return null;
      }
    }

    // Reset the flag after a longer delay for master-detail grids
    // Master-detail grids take longer to restore state completely
    const delay = this.hasMasterDetail ? 1000 : 500;
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.isLoadingGridState = false;
      }, delay);

      // Reset master-detail flag after even longer delay
      if (this.hasMasterDetail) {
        setTimeout(() => {
          this.isMasterDetailStateRestoring = false;
        }, delay + 500);
      }
    });

    // Return the parsed object so DevExtreme can apply visibleIndex and all other state
    return parsedState;
  }

  saveGridState(e: any) {
    // Ensure the state is properly serialized and handle storage quota issues
    try {
      // Filter out column information to preserve fixed column positions
      // Fixed columns should not be reordered by state restoration
      const stateToSave = e ? { ...e } : null;

      if (stateToSave && stateToSave.columns) {
        // Preserve column state (order, width, visibility) so user rearrangements persist,
        // but strip the fixed buttons column (dataField '') to prevent it interfering
        // with position restoration on reload.
        stateToSave.columns = stateToSave.columns.filter(
          (col: any) => col.dataField !== '' && col.name !== ''
        );
      }

      // Let the user preferences service handle optimization and storage
      this.userPreferencesService.saveControlState(this.storageKey, stateToSave);
    } catch (error) {
      console.error('Error saving grid state for', this.storageKey, ':', error);

      // If there are persistent storage issues, try to clear this specific state
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded, clearing state for', this.storageKey);
        this.userPreferencesService.clearControlState(this.storageKey);

        // Optionally notify user about storage issues
        // You could emit an event here to show a notification to the user
        console.warn('Grid state could not be saved due to storage limitations. Some preferences may not be preserved.');
      }
    }
  }

  clearGridState() {
    this.userPreferencesService.saveControlState(this.storageKey, null);
    this.dataGrid.instance.state(null);
  }





 /* onExporting(e: DxDataGridTypes.ExportingEvent) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(this.storageKey);

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${this.storageKey}_Grid.xlsx`);
      });

    });
  }*/

 onExporting(e: DxDataGridTypes.ExportingEvent) {
  this.onExportingToExcelEmitter.emit(e);
}





  getDataSource()
  {
    return this.dataGrid.instance.getDataSource();
  }

  onToolbarPreparing(e: DxDataGridTypes.ToolbarPreparingEvent)
  {
    //console.log(e);
  }

  onDismissFilteredExternallyMessage() {
    this.filteredExternally = false;
    this.clearAllFilters();
    this.onExternalFilterDismissedEmitter.emit();
  }

  public clearAllFilters() {
    this.dataGrid.instance.clearFilter();

    // Re-establish scrollbar synchronization after clearing filters
    this.setupCustomScrollbar();
  }

  public collapseAllGroups() {
    this.dataGrid.instance.collapseAll(-1);// master detail

    const groupCount = this.getGroupDepth();// Get the number of group levels
    for (let i = 0; i < groupCount; i++) {
      this.dataGrid.instance.collapseAll(i);
    }

    localStorage.removeItem(this.expandedRowStorageRowKey);
    localStorage.removeItem(this.selectedRowsStorageKey );
  }

  getGroupDepth(): number {
    const columns = this.dataGrid.instance.getVisibleColumns();
    const groupedColumns = columns.filter(col => col && typeof col.groupIndex === 'number');
    return groupedColumns.length;
  }

 /* async addContextMenuItems(e: any) {
    //console.log('in addContextMenuItems: ', e);
    if (e.row.rowType == 'data') {
      if (!e.items) e.items = [];
      if (this.clickedCellElement && this.clickedCellElement.classList.contains('cell-selected')) {
        this.clickedCellElement.classList.remove('cell-selected');
      }
      e.targetElement.classList.add('cell-selected');
      let targetElement = e.targetElement;
      this.clickedCellElement = targetElement;
      const textToCopy = targetElement.innerText;
      e.items.push({
        text: 'Copy',
        onItemClick: () => {
          navigator.clipboard.writeText(textToCopy);
        },
      });
      e.items.push({
        text: 'Paste',
        onItemClick: () => {
          const fieldToEdit = e.column.dataField;
          if (this.editExclusions.includes(fieldToEdit)) {
            notify(`Cannot paste to ${fieldToEdit}`, 'error', 3000);
            return;
          }
          navigator.clipboard.readText().then((text) => {
            e.row.isEditing = true;
            e.row.isSelected = true;
            e.row.cells[e.columnIndex].isEditing = true;
            e.row.data[fieldToEdit] = text;
            targetElement.innerText = text;
            this.updateCell(e.row.rowIndex, fieldToEdit, text);
          });
        },
      });
    }
  }

  updateCell(rowIndex: number, dataField: string, value: string): void {
    this.dataGrid.instance.cellValue(rowIndex, dataField, value);
  }*/
  editingStart(e: any) { //console.log(e);
    }

  ngOnDestroy() {
    // Clean up selection subscription
    if (this.selectionSubscription) {
      this.selectionSubscription.unsubscribe();
    }

    // Clean up timers
    if (this.scrollbarSetupTimer) {
      clearTimeout(this.scrollbarSetupTimer);
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Stop periodic sync check
    this.stopSyncCheck();

    // Clean up event listeners when component is destroyed
    const globalScrollbar = document.getElementById('globalScrollbar');
    if (globalScrollbar && this.globalScrollElement) {
      globalScrollbar.removeEventListener('scroll', this.boundOnGlobalScroll);
    }
    if (this.gridScrollContainer) {
      this.gridScrollContainer.removeEventListener('scroll', this.boundOnGridScroll);
    }

    // Clean up additional scroll containers
    this.additionalScrollContainers.forEach(container => {
      container.removeEventListener('scroll', this.boundOnGridScroll);
    });
    this.additionalScrollContainers = [];

    // Remove the capture-phase wheel listener registered on the grid root in ngAfterViewInit
    const gridElementForWheelCleanup = this.dataGrid?.instance?.element();
    if (gridElementForWheelCleanup) {
      gridElementForWheelCleanup.removeEventListener('wheel', this.boundOnGridWheel, true);
      gridElementForWheelCleanup.removeEventListener('pointerdown', this.boundOnGridPointerDown, true);
    }
    document.removeEventListener('pointerup', this.boundOnDocumentPointerUp, true);
    clearTimeout(this.pointerSafetyTimeout);
    this.deferredGridUpdates = [];

    // Clean up DevExtreme event listeners
    if (this.dataGrid && this.dataGrid.instance) {
      this.dataGrid.instance.off('contentReady');
    }

    //console.log('🗑️ SIMPLE: Data grid component destroyed:', this.storageKey || 'Unknown');
  }

  // SIMPLE: Public method for manual refresh
  public refreshScrollbar() {
    //console.log('🔄 SIMPLE: Refreshing scrollbar for component:', this.storageKey || 'Unknown');
    this.setupCustomScrollbar();
  }

  /**
   * Compute existing column names to filter out from taskData display.
   * Memoized via ngOnChanges into `existingColumnNames` so the template does
   * not recompute this array on every change detection cycle.
   */
  private computeExistingColumnNames(): string[] {
    if (!this.columnData) {
      return [];
    }

    return this.columnData
      .filter(col => col.title || col.field) // Only include columns with names
      .map(col => col.title || col.field || '') // Get the display name or field name
      .filter(name => name && name.trim() !== '') // Remove empty names
      .concat(['Detail', 'Actions', 'Button', 'Edit', 'Delete']); // Add common action column names
  }

  /**
   * Manual cleanup of grid states when storage issues occur
   */
  public cleanupGridStates(): void {
    if (this.userPreferencesService && typeof this.userPreferencesService.cleanupGridStates === 'function') {
      this.userPreferencesService.cleanupGridStates();
      console.log('Grid states cleaned up for component:', this.storageKey);
    }
  }

  /**
   * Get storage usage information for debugging
   */
  public getStorageInfo(): any {
    if (this.userPreferencesService && typeof this.userPreferencesService.getStorageStats === 'function') {
      return this.userPreferencesService.getStorageStats();
    }
    return null;
  }
}


